import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostListener,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';

import * as moment from 'moment-timezone';
import { forkJoin, Subscription } from 'rxjs';
import { filter, finalize, take } from 'rxjs/operators';

import {
  ClockHour,
  COMPANY_ID_KEY,
  Feature,
  FeatureUser,
  USER_ID_KEY,
} from '@tacliatech/types';
import { AddClockHourService } from '@web-frontend/shared/components/add-clock-hour';
import { ChronoLongtimeService } from '@web-frontend/shared/components/chrono-longtime';
import { FilterComponent } from '@web-frontend/shared/components/filter/filter.component';
import {
  AuthService,
  CalendarResponse,
  ClockHourMetrics,
  ClockHourService,
  StorageService,
} from '@web-frontend/shared/services';
import { getDateStrResum, RemoveEmpty } from '@web-frontend/shared/utils';

import { TranslateService } from '@ngx-translate/core';
import { AmplitudeService } from '@web-frontend/shared/amplitude.service';
import { AddClockHour } from '@web-frontend/shared/components/add-clock-hour/add-clock-hour.types';
import { PermissionService } from '@web-frontend/shared/services/permissions';
import { SandboxService } from '@web-frontend/shared/services/sandbox/sandbox.service';
import { ToastService } from '@web-frontend/shared/services/toast/toast.service';
import { ClockHourView } from './clock-hour.type';
import { ListMobileClockHourAction } from './list-mobile-clock-hour';
import { AnalyticsService } from '@web-frontend/shared/services/analytics/analytics.service';
import AmplitudeEvents from 'src/types/amplitude.enum';
import { formatToCalendar } from './clock-hour-calendar-mapper';
import { CompanyService } from '@web-frontend/shared/services/company';
import { environment } from '@web-frontend/environments';
import { DownloadType } from '@web-frontend/shared/components/download-btn/download-btn.component';
import { FileWriteData } from '@web-frontend/shared/helpers/file-write/file-write.types';
import { FileWriteService } from '@web-frontend/shared/helpers/file-write';

interface OpenTrackingResponse {
  current: string;
  startDate: string;
  timezone: string;
  duration: {
    hour: number;
    minute: number;
    second: number;
  };
}

const WHATSAPP_NUMBER = environment.whatsappNumber;

@Component({
  selector: 'roma-clock-hour',
  templateUrl: './clock-hour.component.html',
  styleUrls: ['./clock-hour.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ClockHourComponent implements OnInit, AfterViewInit, OnDestroy {
  tableMarginTop = '0px';
  distanceToTop = 0;

  @HostListener('window:resize', ['$event'])
  onResize() {
    this.calcTableDistance();
  }

  @HostListener('document:visibilitychange', ['$event'])
  visibilitychange(): void {
    if (this.start && document.visibilityState === 'visible') {
      this.calcCurrentChronometer();
    }
  }

  @ViewChild(FilterComponent)
  filterComponent: FilterComponent;

  columns = ['date', 'entrance', 'durationAverage', 'status', 'note'];
  user$ = this.authService.user$;

  isLoading = true;
  isLoadingMetrics = true;
  clockHours: ClockHour.Calendar[] = [];

  monthlyDays = [];

  currentMonth: number;
  currentDay: number;
  currentYear: number;
  selectedMonth: number;
  remainingMonth: number;

  stringMonth: string;
  stringRemainingMonth: string;

  weekLimit: number;
  weekRemaining: number;

  firstWeekDay: number;
  lastWeekDay: number;
  firstMonthDay: number;
  lastMonthDay: number;
  remainingWeekLength: number;

  addButtonLoading = false;
  view: ClockHourView = ClockHourView.Week;
  clockHourViewRef = ClockHourView;
  featureRef = Feature;
  featureUserRef = FeatureUser;
  userMetrics: ClockHourMetrics = {
    workedHours: null,
    contractHours: null,
    remainingHours: null,
    extraHours: null,
    pendingRegisters: null,
  };

  private hybridRequest = false;

  private searchParams = {
    'idCompanies[]': [StorageService.GetItem(COMPANY_ID_KEY)],
    'idOwners[]': [StorageService.GetItem(USER_ID_KEY)],
    limit: 7,
    from: null,
    to: null,
  };

  private sub$ = new Subscription();
  private flightRequest = new Subscription();

  resize$ = this.sandBoxService.screenBusChannel$;
  //timeTracking variables
  start = false;
  timer;
  seconds = 0;
  openEntry;
  openEntryIndex: number;

  isAdmin = false;
  allowProRegister = true;
  featureManual: string = Feature.SystemPermission.DefaultAllow;

  private calendarId: string;
  private clockInWithStopwatchMandatory: boolean;

  totalHours = '00:00';

  constructor(
    private cdRef: ChangeDetectorRef,
    private i18n: TranslateService,
    private authService: AuthService,
    private clockHourService: ClockHourService,
    private addClockHourService: AddClockHourService,
    private amplitudeService: AmplitudeService,
    private chronoLongtimeService: ChronoLongtimeService,
    private sandBoxService: SandboxService,
    private permissionService: PermissionService,
    private toastService: ToastService,
    private analyticsService: AnalyticsService,
    private companyService: CompanyService,
    private readonly fileWrite: FileWriteService
  ) {}

  ngOnInit(): void {
    this.isAdmin =
      JSON.parse(StorageService.userData)?.role?.toLowerCase() == 'admin_role';
    this.haveFeature();
    this.resolveDateCurrent();
    this.watchRefreshList();
    this.watchTranslate();
    this.getOpenTimeTracking();

    this.loadLocationSettings();
  }

  ngAfterViewInit() {
    this.calcTableDistance();
  }

  ngOnDestroy() {
    this.sub$.unsubscribe();
  }

  public refreshList() {
    this.addClockHourService.refreshList();
  }

  private getHolidayCalendars(): void {
    this.sub$.add(
      this.clockHourService
        .getBankHolidaysCalendars()
        .pipe(
          finalize(() => {
            if (this.calendarId) {
              this.getHolidays();
            }
            this.draw();
          })
        )
        .subscribe((res) => {
          if (Array.isArray(res) && res.length > 0) {
            this.calendarId = res[0].id;
          }
        })
    );
  }

  private getYearsFromClockHours() {
    return this.clockHours
      .map((c) => c.date.year)
      .filter((year, index, self) => self.indexOf(year) === index);
  }

  private getHolidays(): void {
    const years = this.getYearsFromClockHours();
    const holidayRequests = years.map((year) =>
      this.clockHourService.getBankHolidays(this.calendarId, year)
    );

    this.sub$.add(
      forkJoin(holidayRequests)
        .pipe(
          finalize(() => {
            this.draw();
          })
        )
        .subscribe((responses) => {
          const holidays = responses.flat();
          this.clockHours = this.clockHours.map((c) => {
            const holidayDate = holidays.map((h: { day: string }) => {
              const [year, month, day] = h.day.split('-').map(Number);
              return { year, month: month - 1, day };
            });

            return {
              ...c,
              hasHoliday: holidayDate.some(
                (h) =>
                  h.day === c.date.day &&
                  h.month === c.date.month &&
                  h.year === c.date.year
              ),
            };
          });
          this.draw();
        })
    );
  }

  isAvailableFeature = true;

  haveFeature() {
    this.sub$.add(
      this.permissionService
        .hasFeatureFn(Feature.ClockHour.ViewTimer)
        .pipe(take(1))
        .subscribe((res) => {
          this.isAvailableFeature = res;
          if (!this.isAvailableFeature) {
            if (this.isAdmin) {
              this.draw();
            }
            this.getTodayRegisters();
          }
        })
    );
  }

  private formatDDMMYYYY(date: { month: number; year: number; day: number }) {
    const { month, year, day } = date;
    return `${(month + 1)
      .toString()
      .padStart(2, '0')}/${day.toString().padStart(2, '0')}/${year}`;
  }

  getTodayRegisters() {
    const date = getDateStrResum(new Date());
    const parms = {
      idCompany: StorageService.CompanyId,
      date: {
        year: parseInt(date.year),
        month: parseInt(date.month),
        day: parseInt(date.day),
      },
    };

    this.sub$.add(
      this.clockHourService.findTodayRegisters(parms).subscribe((res) => {
        this.allowProRegister = res.registers?.length < res.maxRegisters;
        if (!this.allowProRegister) {
          this.featureManual = Feature.ClockHour.ViewTimer;
        }
      })
    );
  }

  getOpenTimeTracking() {
    this.clockHourService
      .getOpenTimeTracking()
      .subscribe((res: OpenTrackingResponse) => {
        this.resolveLoadData();
        if (res?.duration?.hour > 15) {
          this.chronoLongtimeService.open({
            data: {
              hour: res?.duration?.hour,
              showFooter: false,
            },
          });
        } else {
          this.checkOpenEntry();
        }
      });
  }

  async launchModalClockHour() {
    const today = new Date();

    this.analyticsService.trackEvent({
      eventName: 'trackingTime_start',
      sources: ['amplitude'],
    });

    const date = {
      year: today.getFullYear(),
      month: today.getMonth(),
      day: today.getDate(),
    };

    this.addButtonLoading = true;
    this.draw();

    const search = await this.clockHourService
      .search({
        ...this.searchParams,
        from: JSON.stringify(date),
        to: JSON.stringify(date),
        limit: 1,
      })
      .toPromise();

    this.addButtonLoading = false;
    this.draw();

    const mode = search.docs.length ? 'EDIT' : 'ADD';

    this.addClockHourService
      .open({
        data: {
          mode,
          idCompany: StorageService.CompanyId,
          idOwner: StorageService.UserId,
          params: search.docs?.[0],
          clockInWithStopwatchMandatory: this.clockInWithStopwatchMandatory,
        },
      })
      .subscribe();
  }

  isToday(evt: ClockHour.Output): boolean {
    const today = new Date().toDateString();
    const eventDate = new Date(
      evt.date.year,
      evt.date.month - 1,
      evt.date.day
    ).toDateString();
    return today === eventDate;
  }

  launchModalClockHourEdit(evt: ClockHour.Output): void {
    this.analyticsService.trackEvent({
      eventName: 'trackingTime_list_start',
      sources: ['amplitude'],
      eventProperties: { today: this.isToday(evt) },
    });

    const mode: AddClockHour.Mode = evt?._id ? 'EDIT' : 'ADD';

    this.addClockHourService
      .open({
        data: {
          mode,
          idCompany: StorageService.CompanyId,
          idOwner: StorageService.UserId,
          params: evt,
          clockInWithStopwatchMandatory: this.clockInWithStopwatchMandatory,
        },
      })
      .subscribe();
  }

  isUserAdmin(): boolean {
    return (
      JSON.parse(StorageService.userData).role?.toLowerCase() == 'admin_role'
    );
  }

  deleteClockHour(evt: { _id: string; status?: string }): void {
    if (evt?.status == 'APPROVED' && this.isUserAdmin() == false) {
      this.toastService.show({
        text: this.i18n.instant('clockHour.errorDeleteAccess'),
        type: 'error',
        msDuration: 4000,
      });
      return;
    }

    this.sub$.add(
      this.clockHourService.deleteOne(evt._id).subscribe(() => {
        this.requestClockHours({ loadingGlobal: true });
        const eventData = {
          event: 'trackingTime_delete',
        };
        this.amplitudeService.sendEvent(eventData);
        this.toastService.show({
          text: this.i18n.instant('clockHour.deleteSuccess'),
          type: 'success',
          msDuration: 4000,
        });
        if (evt._id === this.openEntry?._id) {
          clearInterval(this.timer);
          this.start = false;
          this.seconds = 0;
        }
      })
    );
  }

  changeMonth(month: number, action: 'next' | 'prev') {
    if (action === 'next') {
      if (this.selectedMonth == 11) {
        this.selectedMonth = 0;
        this.currentYear = this.currentYear + 1;
      } else {
        this.selectedMonth = month;
      }
    } else {
      if (this.selectedMonth == 0) {
        this.selectedMonth = 11;
        this.currentYear = this.currentYear - 1;
      } else {
        this.selectedMonth = month;
      }
    }
    this.getMonthString();
    this.resolveLoadData();
  }

  goToToday() {
    this.resolveDateCurrent();
    this.selectedMonth = this.currentMonth;
    this.remainingMonth = this.selectedMonth;
    this.getMonthString();
    this.resolveLoadData();

    if (this.view === ClockHourView.Week) {
      const today = new Date();
      const dayOfMonth = today.getDate();
      if (today.getMonth() !== this.selectedMonth) {
        this.selectedMonth = today.getMonth();
        this.currentYear = today.getFullYear();
        this.getMonthString();
      }
      this.resolveWeekFilter(dayOfMonth);
    } else {
      this.resolveMonthFilter();
    }
  }

  changeWeek(day: number, action: 'next' | 'prev') {
    if (action === 'next') {
      if (day > this.lastMonthDay) {
        if (this.selectedMonth == 12) {
          this.selectedMonth = 0;
          this.currentYear = this.currentYear + 1;
        } else {
          this.selectedMonth = this.selectedMonth + 1;
        }
        this.getMonthString();
        this.resolveWeekFilter(1);
      } else {
        if (this.selectedMonth == 12) {
          this.selectedMonth = 0;
          this.currentYear = this.currentYear + 1;
        }
        this.resolveWeekFilter(day);
      }
    } else {
      if (this.hybridRequest) {
        if (this.selectedMonth == 0) {
          this.selectedMonth = 11;
          this.currentYear = this.currentYear - 1;
        } else {
          this.selectedMonth = this.selectedMonth - 1;
          this.getMonthString();
        }
        this.resolveWeekFilter(Math.abs(day - (this.weekLimit + 1)));
      } else {
        if (day <= this.weekLimit + 1) {
          if (day == 1) {
            this.resolveWeekFilter(-6);
          } else {
            this.resolveWeekFilter(this.firstMonthDay);
          }
        } else {
          this.resolveWeekFilter(Math.abs(day - (this.weekLimit + 1)));
        }
      }
    }
  }

  changeClockHourView(view: ClockHourView) {
    if (this.view !== view) {
      this.view = view;
      this.searchParams = {
        ...this.searchParams,
        limit: this.view === ClockHourView.Week ? 7 : 31,
      };
      this.resolveLoadData();

      const eventName =
        this.view === ClockHourView.Week
          ? AmplitudeEvents.trackingTime_weekly
          : AmplitudeEvents.trackingTime_monthly;

      this.analyticsService.trackEvent({
        sources: ['amplitude'],
        eventName: eventName,
      });
    }
  }

  private resolveLoadData() {
    const firstDay = new Date(this.currentYear, this.selectedMonth, 1);
    const lastDay = new Date(this.currentYear, this.selectedMonth + 1, 0);

    this.getMonthString();
    this.firstMonthDay = firstDay.getDate();
    this.lastMonthDay = lastDay.getDate();

    if (this.view === ClockHourView.Week) {
      this.resolveWeekFilter(this.currentDay);
    }
    if (this.view === ClockHourView.Month) {
      this.resolveMonthFilter();
    }
  }

  private getMonthString() {
    moment.locale(this.i18n.currentLang);
    this.stringMonth = moment(
      new Date(this.currentYear, this.selectedMonth + 1, 0)
    )
      .format('MMM')
      .replace('.', '');
    this.stringRemainingMonth = moment(
      new Date(this.currentYear, this.remainingMonth + 1, 0)
    )
      .format('MMM')
      .replace('.', '');
  }

  private changeDate(startDate: Date, endDate: Date) {
    const formattedStartDate = startDate.toLocaleDateString('en-CA');
    const formattedEndDate = endDate.toLocaleDateString('en-CA');

    this.searchParams = {
      ...this.searchParams,
      from: formattedStartDate,
      to: formattedEndDate,
    };

    if (this.searchParams['from'] && this.searchParams['to']) {
      this.requestClockHours({ loadingGlobal: true });
    }
  }

  private requestClockHours({ loadingGlobal = false }) {
    if (loadingGlobal) {
      this.isLoading = true;
      this.draw();
    }

    const query = RemoveEmpty(this.searchParams);
    const userId = StorageService.GetItem(USER_ID_KEY);

    const obs$ = this.clockHourService
      .getUserCalendar(userId, {
        ...query,
      })
      .pipe(
        finalize(() => {
          this.isLoading = false;
          this.updateMetrics();
          this.calcTableDistance();
          this.getHolidayCalendars();
          this.draw();
        })
      );

    if (this.flightRequest) {
      this.flightRequest.unsubscribe();
      this.flightRequest = new Subscription();
    }

    this.flightRequest = obs$.subscribe((res) => {
      this.setClockHours(res);
    });
  }

  private watchTranslate() {
    this.sub$.add(
      this.i18n.onLangChange.subscribe(() => {
        moment.locale(this.i18n.currentLang);
        this.stringMonth = moment(
          new Date(this.currentYear, this.selectedMonth + 1, 0)
        )
          .format('MMM')
          .replace('.', '');
      })
    );
  }

  private resolveDateCurrent() {
    const currentDate = new Date();
    this.currentMonth = currentDate.getMonth();
    this.currentDay = currentDate.getDate();
    this.currentYear = currentDate.getFullYear();
    this.selectedMonth = this.currentMonth;
    this.remainingMonth = this.selectedMonth;
  }

  private setClockHours(response: Record<string, CalendarResponse[]>) {
    this.clockHours = formatToCalendar(response);
    this.getTotalHoursToday(this.clockHours);

    this.getClockHoursMonthEntries();
  }

  private getTotalHoursToday(clockHours: ClockHour.Calendar[]) {
    const total = '00:00';

    const today = this.getTodayClockHours(clockHours);

    if (!today?.histories?.length) {
      this.totalHours = total;
      return;
    }

    const totalHours = this.calculateTotalHours(today.histories);
    this.totalHours = this.formatTotalHours(totalHours);
  }

  private getTodayClockHours(
    clockHours: ClockHour.Calendar[]
  ): ClockHour.Calendar | undefined {
    return clockHours.find((c) => c.date.day === this.currentDay);
  }

  private calculateTotalHours(
    histories: ClockHour.History[]
  ): { hour: number; minute: number } {
    const totalHours = histories.reduce(
      (acc, { duration }) => {
        if (duration) {
          acc.hour += duration.hour;
          acc.minute += duration.minute;
        }
        return acc;
      },
      { hour: 0, minute: 0 }
    );

    totalHours.hour += Math.floor(totalHours.minute / 60);
    totalHours.minute = totalHours.minute % 60;

    return totalHours;
  }

  private formatTotalHours(totalHours: {
    hour: number;
    minute: number;
  }): string {
    return `${String(totalHours.hour).padStart(2, '0')}:${String(
      totalHours.minute
    ).padStart(2, '0')}`;
  }

  private getClockHoursMonthEntries() {
    if (this.view === ClockHourView.Week) {
      this.monthlyDays = [];
      if (this.firstWeekDay > this.lastWeekDay) {
        let i = 0;
        let weekDay = this.firstWeekDay;
        const lastMonthDay: number = this.firstWeekDay + 6 - this.lastWeekDay;
        let day;
        const arr = [];
        for (i; i < 7; i++) {
          if (weekDay <= lastMonthDay) {
            day = this.clockHours.find(
              (clockHour) => clockHour.date.day === weekDay
            );
            if (!day) {
              day = {
                date: {
                  day: weekDay,
                  month: this.selectedMonth - 1,
                  year: this.currentYear,
                },
              };
            }
          } else {
            day = this.clockHours.find(
              (clockHour) => clockHour.date.day === weekDay - lastMonthDay
            );
            if (!day) {
              day = {
                date: {
                  day: weekDay - lastMonthDay,
                  month: this.selectedMonth,
                  year: this.currentYear,
                },
              };
            }
          }

          arr.push(day);
          weekDay++;
        }
        this.monthlyDays = arr;
      } else {
        const monthlyDays = this.clockHours.filter(
          (clockHour) => clockHour.date.month === this.selectedMonth
        );
        this.monthlyDays = monthlyDays;
      }
    } else {
      const monthlyDays = this.clockHours.filter(
        (clockHour) => clockHour.date.month === this.selectedMonth
      );
      this.monthlyDays = monthlyDays;
    }
    this.setTableEntries();
  }

  private setTableEntries() {
    let i: number;
    let tableLength: number;
    let media: number;
    if (this.view === ClockHourView.Week) {
      tableLength = 7;
      media = 5;
      i = 0;
    }
    if (this.view === ClockHourView.Month) {
      i = 1;
      tableLength = this.lastMonthDay + 1;
      media = 20;
    }

    const lastMonthDay: number = this.firstWeekDay + 6 - this.lastWeekDay;

    for (i; i < tableLength; i++) {
      let obj;
      if (this.view === ClockHourView.Week) {
        if (
          this.firstWeekDay > this.lastWeekDay &&
          this.firstWeekDay + i > lastMonthDay
        ) {
          obj = this.monthlyDays.find(
            (o) => o.date.day === this.firstWeekDay + i - lastMonthDay
          );
        } else {
          obj = this.monthlyDays.find(
            (o) => o.date.day === this.firstWeekDay + i
          );
        }
      }
      if (this.view === ClockHourView.Month) {
        obj = this.monthlyDays.find((o) => o.date.day === i);
      }
      if (!obj) {
        if (this.view === ClockHourView.Week) {
          if (this.remainingWeekLength >= 0 && this.hybridRequest) {
            if (i <= this.remainingWeekLength) {
              this.monthlyDays.push({
                date: {
                  year:
                    this.selectedMonth == 0
                      ? this.currentYear - 1
                      : this.currentYear,
                  month: this.selectedMonth == 0 ? 11 : this.remainingMonth,
                  day: this.firstWeekDay + i,
                },
              });
            } else {
              this.monthlyDays.push({
                date: {
                  year:
                    this.selectedMonth == 12
                      ? this.currentYear + 1
                      : this.currentYear,
                  month: this.selectedMonth == 12 ? 0 : this.selectedMonth,
                  day: i - this.remainingWeekLength,
                },
              });
            }
          } else {
            this.monthlyDays.push({
              date: {
                year: this.currentYear,
                month: this.selectedMonth,
                day: this.firstWeekDay + i,
              },
            });
          }
        }
        if (this.view === ClockHourView.Month) {
          this.monthlyDays.push({
            date: { year: this.currentYear, month: this.selectedMonth, day: i },
          });
        }
      }
    }
  }

  private resolveWeekFilter(currentDay: number) {
    if (currentDay == -6) {
      currentDay = new Date(
        this.currentYear,
        this.selectedMonth,
        currentDay
      ).getDate();
      if (this.selectedMonth == 0) {
        this.selectedMonth = 11;
        this.currentYear = this.currentYear - 1;
      } else {
        this.selectedMonth = this.selectedMonth - 1;
        this.getMonthString();
      }
    }

    const weekDate = new Date(this.currentYear, this.selectedMonth, currentDay);
    const weekDay = weekDate.toLocaleDateString('en-US', { weekday: 'long' });

    const weekDayMapping = {
      Monday: { weekLimit: 6, weekRemaining: 0 },
      Tuesday: { weekLimit: 5, weekRemaining: 1 },
      Wednesday: { weekLimit: 4, weekRemaining: 2 },
      Thursday: { weekLimit: 3, weekRemaining: 3 },
      Friday: { weekLimit: 2, weekRemaining: 4 },
      Saturday: { weekLimit: 1, weekRemaining: 5 },
      Sunday: { weekLimit: 0, weekRemaining: 6 },
    };
    const { weekLimit = 0, weekRemaining = 0 } = weekDayMapping[weekDay] || {};
    this.weekLimit = weekLimit;
    this.weekRemaining = weekRemaining;

    if (currentDay - this.weekRemaining < 1) {
      this.firstWeekDay = new Date(
        this.currentYear,
        this.selectedMonth,
        currentDay - this.weekRemaining
      ).getDate();
      this.lastWeekDay = this.weekLimit + currentDay;

      const remainingLastDay = new Date(
        this.currentYear,
        this.selectedMonth,
        0
      ).getDate();
      this.remainingMonth = new Date(
        this.currentYear,
        this.selectedMonth,
        currentDay - this.weekRemaining
      ).getMonth();
      this.remainingWeekLength = remainingLastDay - this.firstWeekDay;
      this.hybridRequest = true;

      this.changeDate(
        new Date(
          this.selectedMonth == 0 ? this.currentYear - 1 : this.currentYear,
          this.remainingMonth,
          this.firstWeekDay
        ),
        new Date(this.currentYear, this.selectedMonth, this.lastWeekDay)
      );
    } else {
      this.firstWeekDay = currentDay - this.weekRemaining;
      this.lastWeekDay = currentDay + this.weekLimit;
      const remainingLastDay = new Date(
        this.currentYear,
        this.selectedMonth + 1,
        0
      ).getDate();
      if (this.lastWeekDay > remainingLastDay) {
        this.hybridRequest = true;

        this.remainingMonth = this.selectedMonth;
        this.remainingWeekLength = remainingLastDay - this.firstWeekDay;
        this.lastWeekDay = this.lastWeekDay - remainingLastDay;

        this.changeDate(
          new Date(
            this.currentYear,
            this.selectedMonth,
            currentDay - this.weekRemaining
          ),
          new Date(this.currentYear, this.selectedMonth + 1, this.lastWeekDay)
        );
        this.selectedMonth = this.selectedMonth + 1;
        this.lastMonthDay = new Date(
          this.currentYear,
          this.selectedMonth + 1,
          0
        ).getDate();
      } else {
        this.remainingWeekLength = 0;
        this.hybridRequest = false;
        this.changeDate(
          new Date(
            this.currentYear,
            this.selectedMonth,
            currentDay - this.weekRemaining
          ),
          new Date(this.currentYear, this.selectedMonth, this.lastWeekDay)
        );
      }
    }
    this.getMonthString();
  }

  private resolveMonthFilter() {
    this.changeDate(
      new Date(this.currentYear, this.selectedMonth, this.firstMonthDay),
      new Date(this.currentYear, this.selectedMonth, this.lastMonthDay)
    );
  }

  private draw() {
    this.cdRef.detectChanges();
  }

  private watchRefreshList() {
    this.sub$.add(
      this.addClockHourService.refreshList$
        .pipe(filter((res) => Boolean(res)))
        .subscribe(() => {
          this.requestClockHours({ loadingGlobal: true });
        })
    );
  }

  startTimeTracking() {
    if (!this.start) {
      this.start = true;
      this.draw();
    }

    this.timer = setInterval(() => {
      if (!this.start) {
        this.start = true;
      }
      this.seconds++;
      this.draw();
    }, 1000);
    this.requestClockHours({ loadingGlobal: false });
  }

  deleteLessThanOneMinuteTracking() {
    let data;
    if (this.openEntry?.histories?.length === 1) {
      //if only 1 record delete all
      this.clockHourService.deleteOne(this.openEntry?._id).subscribe(() => {
        this.afterDeleteLessThanOneMinuteTracking();
      });
    } else {
      //if more than 1 record only delete the records without endDate
      const itemsForDelete = this.openEntry.histories.filter(
        (fil) => !fil.endDate
      );
      if (itemsForDelete.length > 0) {
        for (let i = 0; i < itemsForDelete.length; i++) {
          this.openEntry.histories.splice(
            this.openEntry.histories.indexOf(itemsForDelete[i]),
            1
          );
        }
      }

      const notes = this.formatNotes(this.openEntry.notes);

      data = {
        idCreatedBy: StorageService.GetItem(USER_ID_KEY),
        idOwner: StorageService.GetItem(USER_ID_KEY),
        idCompany: StorageService.GetItem(COMPANY_ID_KEY),
        date: this.openEntry.date,
        histories: this.openEntry.histories,
        notes: notes,
        deleted: false,
        approved: this.openEntry.approved,
      };

      this.clockHourService
        .updateOne(this.openEntry._id, data)
        .subscribe(() => {
          this.afterDeleteLessThanOneMinuteTracking();
        });
    }
  }

  afterDeleteLessThanOneMinuteTracking() {
    this.requestClockHours({ loadingGlobal: false });
    const eventData = {
      event: 'trackingTime_auto_delete',
    };
    this.amplitudeService.sendEvent(eventData);
  }

  checkOpenEntry() {
    this.clockHourService
      .searchOpenEntry({
        idOwner: StorageService.GetItem(USER_ID_KEY),
      })
      .subscribe((res: any) => {
        if (res.payload) {
          this.openEntry = res.payload;
          this.openEntryIndex = this.openEntry?.histories.findIndex(
            (entry) => !entry.endHour || entry.endHour.hour === null
          );
          if (this.openEntryIndex === -1) {
            return;
          }
          this.calcCurrentChronometer();
        }
      });
  }

  calcCurrentChronometer() {
    if (!this.openEntry) return;
    const date = new Date();
    const hour = date.getHours();
    const minutes = date.getMinutes();
    const seconds = date.getSeconds();

    const time =
      this.openEntry?.histories[this.openEntryIndex].startHour.hour
        .toString()
        .padStart(2, '0') +
      ':' +
      this.openEntry?.histories[this.openEntryIndex].startHour.minute
        .toString()
        .padStart(2, '0') +
      ':' +
      (this.openEntry?.histories[this.openEntryIndex].startHour.second
        ? this.openEntry?.histories[this.openEntryIndex].startHour.second
            .toString()
            .padStart(2, '0')
        : '00');
    const dat = this.formatDDMMYYYY(this.openEntry.date);
    const currentDat = this.formatDDMMYYYY({
      year: date.getFullYear(),
      month: date.getMonth(),
      day: date.getDate(),
    });

    const currentDuration = ClockHour.getDifferenceHours(
      (hour + ':' + minutes + ':' + seconds) as string,
      time as string,
      'SUB',
      dat,
      currentDat
    );

    const chronoSeconds =
      currentDuration.hour * 3600 +
      currentDuration.minute * 60 +
      currentDuration.seconds;
    this.seconds = Math.trunc(chronoSeconds);
    if (!this.start) {
      this.startTimeTracking();
    }
  }

  makeMobileActions(evt: ListMobileClockHourAction) {
    if (evt.action === 'SIGN') {
      const params: any = {
        date: evt.date,
      };

      const mode = evt?.id ? 'EDIT' : 'ADD';

      this.addClockHourService
        .open({
          data: {
            mode,
            idCompany: StorageService.CompanyId,
            idOwner: StorageService.UserId,
            params: {
              ...params,
              ...evt?.item,
            },
            clockInWithStopwatchMandatory: this.clockInWithStopwatchMandatory,
          },
        })
        .subscribe();
    }

    if (evt.action === 'DELETE') {
      this.deleteClockHour({ _id: evt?.id });
    }
  }

  public refresh() {
    return this.draw();
  }

  calcTableDistance() {
    const containerTable = document.getElementById('containerTable');
    this.distanceToTop = containerTable?.getBoundingClientRect().top - 74;
  }

  private shouldIgnoreResize(scrollingUp, el) {
    if (!this.ignoreResize) return false;

    if (scrollingUp) {
      el.scrollTop = this.minScroll;
    } else {
      this.ignoreResize = false;
      this.isFirstScroll = false;
    }

    return true;
  }

  private setDefaultTableStyles(isShort) {
    this.tableMarginTop = '0px';
    this.tableStyles = isShort
      ? { display: 'inline-block', height: '90vh' }
      : null;
  }

  private handleNearTopScroll(el, isShort, scrollingUp) {
    if (isShort && scrollingUp) {
      if (window.innerWidth > 1000) {
        this.tableMarginTop = '-180px';
      } else {
        this.tableMarginTop = '-240px';
      }
      if (this.isFirstScroll) {
        el.scrollTop = 5;
        this.ignoreResize = true;
      }
    } else {
      this.tableMarginTop = `-${el.scrollTop}px`;
      this.isFirstScroll = true;
    }
  }

  tableStyles;
  minScroll = 5;
  ignoreResize = false;
  isFirstScroll = true;
  checkScroll(e) {
    const el = e.target;
    const isShort = el.scrollHeight < 1080;
    const scrollingUp = el.scrollTop > this.minScroll;

    if (this.shouldIgnoreResize(scrollingUp, el)) return;

    this.setDefaultTableStyles(isShort);

    if (el.scrollTop <= this.distanceToTop) {
      this.handleNearTopScroll(el, isShort, scrollingUp);
    } else {
      this.tableMarginTop = `-${this.distanceToTop}px`;
    }
  }

  get isLowerThan1450px() {
    return window.innerWidth < 1450;
  }

  handleDownloadFile(payload: { data: FileWriteData }): void {
    const { blob, path } = payload.data;
    this.fileWrite.writeFile({
      path,
      blob,
    });
  }

  download(type: string): void {
    const downloadActions = {
      [DownloadType.EXCEL]: {
        event: AmplitudeEvents.trackingTime_excel_download,
      },
      [DownloadType.PDF]: {
        event: AmplitudeEvents.trackingTime_pdf_download,
      },
    };

    this.clockHourService
      .download(type, this.searchParams, {
        id: StorageService.UserId,
        name: JSON.parse(StorageService.userData)?.name,
      })
      .then(() => {
        this.analyticsService.trackEvent({
          sources: ['amplitude'],
          eventName: downloadActions[type].event,
        });
      });
  }

  updateMetrics(): void {
    this.isLoadingMetrics = true;
    this.cdRef.detectChanges();
    const dateFrom = this.searchParams.from;
    const dateTo = this.searchParams.to;
    const userId = StorageService.UserId;

    this.clockHourService.getUserMetrics(userId, dateFrom, dateTo).subscribe({
      next: (res) => {
        this.userMetrics = res;
        this.isLoadingMetrics = false;
        this.cdRef.detectChanges();
      },
      error: () => {
        this.isLoadingMetrics = false;
        this.cdRef.detectChanges();
      },
    });
  }

  formatNotes(notesArray) {
    const notes = notesArray?.map((note) => {
      return {
        text: note?.text ?? note,
        idAuthor: StorageService.GetItem(USER_ID_KEY),
      };
    });
    return notes || [];
  }

  loadLocationSettings(): void {
    this.isLoading = true;
    this.draw();
    this.companyService.findOne(StorageService.CompanyId).subscribe({
      next: (res) => {
        this.clockInWithStopwatchMandatory =
          res?.trackingTimeSettings?.clockInWithStopwatchMandatory;
        this.showWhatsAppNotification(res);
        this.isLoading = false;
        this.draw();
      },
      error: () => {
        this.draw();
      },
    });
  }

  showWhatsAppNotification(res) {
    const alreadyShown =
      localStorage.getItem('whatsappNotification') === 'true';

    if (!alreadyShown && res?.trackingTimeSettings?.whatsappClockInEnabled) {
      this.toastService.show({
        text: 'clockHour.whatsappNotification.title',
        description: this.i18n.instant(
          'clockHour.whatsappNotification.description',
          { number: WHATSAPP_NUMBER }
        ),
        type: 'info',
        showClose: true,
        msDuration: 600000,
        link: 'clockHour.whatsappNotification.cta',
        linkData: () => this.handleWhatsAppLink(),
      });

      localStorage.setItem('whatsappNotification', 'true');
    }
  }

  handleWhatsAppLink() {
    this.analyticsService.trackEvent({
      eventName: AmplitudeEvents.whatsapp_stopwatch_openchat,
      sources: ['amplitude'],
    });
    window.open(`https://api.whatsapp.com/send?phone=${WHATSAPP_NUMBER}`);
  }
}
